<script>
  // Keypair generation for an SFTP credential. Shared by two callers with
  // different persistence: the bucket list stores the key on a saved
  // credential immediately, the bucket editor page appends it to an unsaved
  // draft. Neither difference belongs here -- the caller passes `generate`,
  // and this component owns the type/size/comment form plus the once-only
  // private-key reveal, which is the part both need identically.
  import { ui } from '../lib/ui.svelte.js';
  import TextField from './TextField.svelte';

  let { subject = 'this credential', defaultComment = '', generate, onClose } = $props();

  // Bit sizes per type (ed25519 is fixed-size). Note: ECDSA's largest curve is
  // P-521 (521 bits), not 512. First entry is the default.
  const KEY_SIZES = { ecdsa: [256, 521], rsa: [2048, 4096] };

  let type = $state('ed25519');
  let bits = $state(0); // 0 = server default; set when the type has sizes
  // Seeded once, deliberately: the operator types in this field, so it must not
  // snap back to the username if the parent re-renders. A fresh dialog is
  // mounted per open, which is what makes a one-time read the whole intent --
  // read through a closure so the compiler knows that is meant.
  let comment = $state((() => defaultComment)());
  let result = $state(null); // { private_key, public_key, fingerprint, type }
  let busy = $state(false);

  function selectType(t) {
    type = t;
    bits = KEY_SIZES[t]?.[0] ?? 0; // default to the first size, 0 for ed25519
  }

  async function run() {
    busy = true;
    try {
      result = await generate(type, bits, comment.trim());
      ui.snack('Keypair generated — copy the private key now', 'success', 3000);
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { busy = false; }
  }

  function copy(text, label) {
    navigator.clipboard?.writeText(text);
    ui.snack(label, 'success', 1500);
  }

  function download() {
    const blob = new Blob([result.private_key], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `id_${result.type.replace(/[^a-z0-9]/gi, '_')}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // Escape closes. Owned here so neither caller has to route key events for a
  // dialog it doesn't render itself.
  function onKey(e) { if (e.key === 'Escape') onClose(); }
</script>

<svelte:window onkeydown={onKey} />

<div class="sftp-dlg-scrim" onclick={onClose} role="presentation"></div>
<div class="sftp-dlg-wrap" role="dialog" aria-modal="true" aria-labelledby="sftp-keygen-title">
  <div class="sftp-dlg">
    <h2 id="sftp-keygen-title" class="sftp-dlg-title">Generate keypair</h2>
    <p class="text-sm text-on-surface-variant mb-3">
      For <span class="font-mono">{subject}</span>. The private key is shown <strong>once</strong> —
      copy or download it now; only the public key is stored.
    </p>
    {#if !result}
      <div class="sftp-keytype">
        {#each ['ed25519', 'ecdsa', 'rsa'] as t}
          <button type="button" class="sftp-keytype-btn" class:selected={type === t} onclick={() => selectType(t)}>{t}</button>
        {/each}
      </div>
      {#if KEY_SIZES[type]}
        <div class="sftp-keysize">
          <span class="sftp-keysize-label">Bits</span>
          {#each KEY_SIZES[type] as b}
            <button type="button" class="sftp-keytype-btn" class:selected={bits === b} onclick={() => (bits = b)}>{b}</button>
          {/each}
        </div>
      {/if}
      <div class="sftp-gencomment">
        <TextField label="Key name / comment" bind:value={comment} spellcheck="false" autocapitalize="none" />
        <p class="sftp-cred-hint">
          <span class="material-symbols-outlined">info</span>
          Appended to the end of the public key (e.g. <span class="font-mono">ssh-ed25519 AAAA… {comment.trim() || 'name'}</span>).
          Handy for telling keys apart. Defaults to the username.
        </p>
      </div>
      <div class="sftp-dlg-actions">
        <button type="button" onclick={onClose} class="sftp-dlg-btn">Cancel</button>
        <button type="button" onclick={run} disabled={busy} class="sftp-dlg-btn sftp-dlg-btn-primary">{busy ? 'Generating…' : 'Generate'}</button>
      </div>
    {:else}
      <div class="sftp-keyout">
        <div class="sftp-keyout-label">Private key ({result.type})</div>
        <pre class="sftp-keyout-pre">{result.private_key}</pre>
        <div class="sftp-keyout-actions">
          <button type="button" onclick={() => copy(result.private_key, 'Private key copied')} class="sftp-dlg-btn">
            <span class="material-symbols-outlined text-base align-middle">content_copy</span> Copy
          </button>
          <button type="button" onclick={download} class="sftp-dlg-btn">
            <span class="material-symbols-outlined text-base align-middle">download</span> Download
          </button>
        </div>
        <div class="sftp-keyout-label mt-2">Fingerprint</div>
        <div class="font-mono text-xs break-all">{result.fingerprint}</div>
      </div>
      <div class="sftp-dlg-actions">
        <button type="button" onclick={onClose} class="sftp-dlg-btn sftp-dlg-btn-primary">Done</button>
      </div>
    {/if}
  </div>
</div>

<style>
  /* Shared M3 basic-dialog look, same as the app's other dialogs. */
  .sftp-dlg-scrim { position: fixed; inset: 0; background: rgb(0 0 0 / 0.32); z-index: 1200; }
  .sftp-dlg-wrap { position: fixed; inset: 0; z-index: 1201; display: flex; align-items: center; justify-content: center; padding: 16px; pointer-events: none; }
  .sftp-dlg { pointer-events: auto; inline-size: 100%; max-inline-size: 640px; max-block-size: calc(100dvh - 32px); overflow-y: auto; background: rgb(var(--md-sys-color-surface-container-high)); --md-tf-surface: var(--md-sys-color-surface-container-high); color: rgb(var(--md-sys-color-on-surface)); border-radius: 28px; padding: 24px; box-shadow: 0 6px 10px 0 rgba(0,0,0,0.30), 0 12px 24px 8px rgba(0,0,0,0.15); }
  .sftp-dlg-title { font-size: 24px; line-height: 32px; font-weight: 400; margin: 0 0 16px; }
  .sftp-dlg-actions { display: flex; justify-content: flex-end; gap: 8px; margin-block-start: 20px; }
  .sftp-dlg-btn { min-block-size: 44px; padding: 0 16px; border: 0; border-radius: 22px; font-size: 14px; font-weight: 500; color: rgb(var(--md-sys-color-primary)); background: transparent; cursor: pointer; }
  .sftp-dlg-btn:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .sftp-dlg-btn-primary { padding: 0 24px; background: rgb(var(--md-sys-color-primary)); color: rgb(var(--md-sys-color-on-primary)); }
  .sftp-dlg-btn-primary:hover { opacity: 0.9; }
  .sftp-dlg-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .sftp-cred-hint { display: flex; align-items: center; gap: 4px; font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant)); margin: 0; }
  .sftp-cred-hint .material-symbols-outlined { font-size: 16px; }

  .sftp-keytype { display: flex; gap: 0.5rem; margin-block-end: 0.5rem; }
  .sftp-keysize { display: flex; align-items: center; gap: 0.5rem; margin-block-end: 0.5rem; }
  .sftp-keysize-label { font-size: 0.75rem; color: rgb(var(--md-sys-color-on-surface-variant)); min-inline-size: 2rem; }
  .sftp-keytype-btn { flex: 1; min-block-size: 44px; padding: 0.5rem; border: 1px solid rgb(var(--md-sys-color-outline-variant)); border-radius: 12px; background: transparent; color: rgb(var(--md-sys-color-on-surface-variant)); font-size: 0.85rem; font-weight: 500; cursor: pointer; }
  .sftp-keytype-btn.selected { background: rgb(var(--md-sys-color-primary-container)); color: rgb(var(--md-sys-color-on-primary-container)); border-color: transparent; }
  .sftp-gencomment { margin-block: 0.75rem 0.5rem; display: flex; flex-direction: column; gap: 0.375rem; }
  .sftp-keyout-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(var(--md-sys-color-on-surface-variant)); margin-block-end: 4px; }
  .sftp-keyout-pre { background: rgb(var(--md-sys-color-surface)); border-radius: 8px; padding: 0.625rem; font-family: 'Menlo','Consolas',monospace; font-size: 11px; line-height: 1.4; white-space: pre-wrap; word-break: break-all; max-block-size: 220px; overflow: auto; margin: 0; }
  .sftp-keyout-actions { display: flex; gap: 0.5rem; margin-block-start: 0.5rem; }
</style>
